import './utils/setWebpackPublicPathFromSsrInjectedPath';
import { createBrowserHistory } from 'history';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import createSagaMiddleware from 'redux-saga';
import { ConnectedRouter, routerMiddleware } from 'connected-react-router';
import debug from 'debug';
import { insertCss } from 'utils/css';
import ScrollToTop from 'components/nav/scrollToTop';

import App from 'components/app';
import StyleContext from 'isomorphic-style-loader/StyleContext';
import defaultState from 'config/defaultState';
import createRootReducer from 'reducers';
import rootSaga, { requestSiteOptions } from 'sagas';
import locateRemoteService from 'services/locateRemoteService';
import createHistoryWithInProductQueryParam from './utils/createHistoryWithInProductQueryParam';
import trackingMiddleware from './middlewares/tracking';

if (process.env.REACT_APP_DEBUG) {
  debug.enable(process.env.REACT_APP_DEBUG);
}

const history = createHistoryWithInProductQueryParam(createBrowserHistory());

const canHotReload = 'production' !== process.env.NODE_ENV && module.hot;
const sagaMiddleware = createSagaMiddleware();

const enhancer = composeWithDevTools(
  applyMiddleware(
    routerMiddleware(history),
    sagaMiddleware,
    trackingMiddleware,
  ),
);

const state = window.__PRELOADED_STATE__ || defaultState; // eslint-disable-line no-underscore-dangle
const store = createStore(createRootReducer(history), state, enhancer);

renderApp();

async function renderApp() {
  const service = await locateRemoteService();
  const rootEl = document.getElementById('root');

  // This will technically block the app from rendering which may cause a FOUC
  // UX, but the time to execute this will be minimal if the app was pre-rendered
  // on the server, because the site options will already be available.
  try {
    // The app depends on site options being available in the state container.
    await sagaMiddleware.run(requestSiteOptions, service).done;
  } catch (err) {
    // If an error has occurred then some critical system has failed, and it
    // is impossible for the app to render or function normally.
    // TODO: Show a more user friendly error message
    ReactDOM.render(<p>Please try again later.</p>, rootEl);

    return undefined;
  }

  sagaMiddleware.run(rootSaga, service);

  if (canHotReload) {
    module.hot.accept('./reducers', () => store.replaceReducer(createRootReducer(history)));
  }

  const renderOrHydrate = canHotReload ? ReactDOM.render : ReactDOM.hydrate;

  renderOrHydrate(
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <StyleContext.Provider value={{ insertCss }}>
          <ScrollToTop>
            <App />
          </ScrollToTop>
        </StyleContext.Provider>
      </ConnectedRouter>
    </Provider>,
    rootEl,
  );

  if (canHotReload) {
    module.hot.accept('./components/app', () => {
      const NextApp = require('./components/app').default; // eslint-disable-line global-require
      ReactDOM.render(
        <Provider store={store}>
          <ConnectedRouter history={history}>
            <StyleContext.Provider value={{ insertCss }}>
              <NextApp />
            </StyleContext.Provider>
          </ConnectedRouter>
        </Provider>,
        rootEl,
      );
    });
  }

  return undefined;
}
